/**
 * File browser (Feature #3 + Block 3). A list view with rename/move/delete via
 * a right-click menu, drag & drop into folders, and per-folder MCP-token
 * creation. Every mutation is re-checked server-side by the central ACL guard;
 * the UI gating (greying out entries the caller can't perform) is convenience.
 */

import { Fragment, useCallback, useEffect, useState } from "react";

import { ApiError, ryfeApi } from "../api";
import { serializeScene } from "../board-data";
import {
  createUrlBookmark,
  openStoredFile,
  uploadFileToFolder,
  uploadFolderToFolder,
} from "../file-actions";
import { extensionOf } from "../upload-map";
import { routeForKind } from "../open-file";
import { useRyfe } from "../RyfeContext";
import { navigate } from "../router";
import { sortRows, useTableSort } from "../table-sort";
import { applyFilter, EMPTY_FILTER, type FilterConfig } from "../table-filter";

import { createEncryptedSnapshot } from "../share";

import { KiFolderDialog } from "./KiFolderDialog";
import { MoveDialog } from "./MoveDialog";
import { CreateTokenDialog } from "./CreateTokenDialog";
import { ShareSettingsDialog } from "./ShareSettingsDialog";
import { ShareSubmenu } from "./ShareSubmenu";
import { SortableTh } from "./SortableTh";
import { FilterBar } from "./FilterBar";
import { AddFileDialog, type NewEntryKind } from "./AddFileDialog";
import { RyfeSharedWithMe } from "./RyfeSharedWithMe";
import { SelectConfigEditor, type SelectOpt } from "./SelectConfigEditor";

import "./ryfe.scss";

import type {
  AclRole,
  FileCategory,
  FileColumn,
  FileMeta,
  FolderChild,
  FolderListing,
  TemplateFile,
} from "../api";

// v1.6 / D-KI (AP-KI-Tags) — the closed category set (mirrors the DB CHECK in
// migration 028; the tag reaches Dify as an input for tag-dependent workflows).
const KI_CATEGORIES = [
  "Artefakt",
  "General Knowledge",
  "Handover Document",
  "User generated",
  "Project Knowledge",
] as const;

type RowKind = "folder" | "file";
interface Target {
  kind: RowKind;
  id: string;
  name: string;
  role: AclRole | null | undefined;
  // CC_11-Revision — the file's kind + template marking (files only).
  fileKind?: string;
  templateRole?: string | null;
}

const fmtSize = (n?: number) => {
  if (n === undefined || n === null) {
    return "—";
  }
  if (n < 1024) {
    return `${n} B`;
  }
  if (n < 1024 * 1024) {
    return `${(n / 1024).toFixed(1)} KB`;
  }
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};
const fmtDate = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString() : "—";

const canWriteRole = (r: AclRole | null | undefined) =>
  r === "editor" || r === "owner";
const canManageRole = (r: AclRole | null | undefined) => r === "owner";

// AP-7 (P17.1) — the „Typ" shown for a file. Editor-less uploads (kind='file')
// have no meaningful kind name, so their type is the file EXTENSION (PDF, XLSX,
// …) taken from the name — automatic, no extra column. Editor kinds keep their
// kind label (board/md/list/image/url).
const typeLabel = (f: FileMeta): string => {
  if (f.kind === "file") {
    const ext = extensionOf(f.name);
    return ext ? ext.toUpperCase() : "Datei";
  }
  return f.kind;
};

export const RyfeFiles = ({ folderId }: { folderId?: string }) => {
  const { workspace } = useRyfe();
  const wsId = workspace?.id;
  const [listing, setListing] = useState<FolderListing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number; t: Target } | null>(
    null,
  );
  const [moving, setMoving] = useState<Target | null>(null);
  // AP2·P3-4 — the folder whose KI-Ordner settings dialog is open.
  const [kiFolder, setKiFolder] = useState<FolderChild | null>(null);
  const [tokenFolder, setTokenFolder] = useState<{
    id: string;
    name: string;
  } | null>(null);
  // V1.2 Block 4 — target of the open Freigabe-Dialog (folder or file row).
  const [shareTarget, setShareTarget] = useState<{
    kind: "folder" | "file";
    id: string;
    name: string;
  } | null>(null);
  // CC_11-Revision — the EXTERNAL share submenu (Mail + Snapshot) per file row,
  // same dialog as on the board/list/note pages.
  const [shareMenu, setShareMenu] = useState<Target | null>(null);
  // CC_11 (D-62/D-63) — templates for the "Aus Vorlage" tab of AddFileDialog.
  const [templates, setTemplates] = useState<TemplateFile[]>([]);
  const [dragOver, setDragOver] = useState<string | null>(null);
  // v1.6 / D-KI (AP4) — workspace-shared custom columns on „Meine Dateien".
  const [fileCols, setFileCols] = useState<FileColumn[]>([]);
  const [fileVals, setFileVals] = useState<
    Record<string, Record<string, unknown>>
  >({});
  const [addingCol, setAddingCol] = useState(false);
  const [editingCol, setEditingCol] = useState<FileColumn | null>(null);
  const [colMenu, setColMenu] = useState<string | null>(null);
  // v1.6 / D-KI (AP-KI-Tags) — per-file category tag + the open metadata editor.
  const [categories, setCategories] = useState<Record<string, FileCategory>>(
    {},
  );
  const [metaTarget, setMetaTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  // v1.8 AP-5 (P21) — the user's favorite file-ids in this workspace, for the
  // ★-marking + the row menu toggle.
  const [favIds, setFavIds] = useState<Set<string>>(new Set());
  // Welle A (Datei-Master) — inline folder expansion (tree). One folder open at
  // a time; `path` lets us drill deeper within the expanded row (same guarded
  // listing endpoint). Clicking the folder NAME still navigates into the full
  // view; the ▸ chevron expands in place.
  const [expand, setExpand] = useState<{
    id: string;
    path: { id: string; name: string }[];
    listing: FolderListing | null;
  } | null>(null);

  const load = useCallback(async () => {
    if (!wsId) {
      return;
    }
    setError(null);
    try {
      const l = folderId
        ? await ryfeApi.listFolder(wsId, folderId)
        : await ryfeApi.getRoot(wsId);
      setListing(l);
      // v1.8 AP-5 (P21) — the user's favorite ids (best-effort; a failure just
      // leaves stars unmarked).
      ryfeApi
        .listFavoriteIds(wsId)
        .then((r) => setFavIds(new Set(r.ids)))
        .catch(() => undefined);
      // AP4 — load the user's custom columns + their (shared) values for the
      // files in this folder. Failures here must not blank the file list.
      try {
        const fileIds = l.files.map((f) => f.id);
        // v1.7 — columns are folder-scoped: ask for the ones visible in the
        // folder currently open (l.id is the root id at the top level), so a
        // column created in a subfolder never leaks into its parents.
        const [cols, vals, cats] = await Promise.all([
          ryfeApi.listFileColumns(wsId, l.id),
          fileIds.length
            ? ryfeApi.getFileColumnValues(wsId, fileIds, l.id)
            : Promise.resolve({ values: {} }),
          // AP-KI-Tags — the per-file category tag for the list.
          fileIds.length
            ? ryfeApi.getFileCategories(wsId, fileIds)
            : Promise.resolve({ metadata: {} }),
        ]);
        setFileCols(cols.columns);
        setFileVals(vals.values);
        setCategories(cats.metadata);
      } catch {
        /* columns/tags are a decoration — ignore if they fail to load */
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }, [wsId, folderId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveFileCell = async (
    fileId: string,
    col: FileColumn,
    value: unknown,
  ) => {
    if (!wsId) {
      return;
    }
    try {
      await ryfeApi.setFileColumnValue(wsId, fileId, col.id, value);
      setFileVals((prev) => ({
        ...prev,
        [fileId]: { ...(prev[fileId] || {}), [col.id]: value },
      }));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const deleteFileColumn = async (col: FileColumn) => {
    if (!wsId) {
      return;
    }
    setColMenu(null);
    try {
      await ryfeApi.deleteFileColumn(wsId, col.id);
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // v1.8 AP-5 (P21) — toggle a file's favorite flag (per-user). Optimistic set
  // update; reverts on failure.
  const toggleFavorite = async (t: Target) => {
    if (!wsId || t.kind !== "file") {
      return;
    }
    const on = favIds.has(t.id);
    setFavIds((prev) => {
      const next = new Set(prev);
      if (on) {
        next.delete(t.id);
      } else {
        next.add(t.id);
      }
      return next;
    });
    try {
      if (on) {
        await ryfeApi.removeFavorite(wsId, t.id);
      } else {
        await ryfeApi.addFavorite(wsId, t.id);
      }
    } catch {
      // revert
      setFavIds((prev) => {
        const next = new Set(prev);
        if (on) {
          next.add(t.id);
        } else {
          next.delete(t.id);
        }
        return next;
      });
    }
  };

  // Welle A — toggle inline expansion of a folder row (tree preview).
  const toggleExpand = async (f: FolderChild) => {
    if (!wsId) {
      return;
    }
    if (expand?.id === f.id) {
      setExpand(null);
      return;
    }
    const path = [{ id: f.id, name: f.name }];
    setExpand({ id: f.id, path, listing: null });
    try {
      const l = await ryfeApi.listFolder(wsId, f.id);
      setExpand({ id: f.id, path, listing: l });
    } catch {
      setExpand(null);
      setError("Ordner kann nicht geöffnet werden.");
    }
  };
  // Drill deeper inside the expanded row, or jump back via the path.
  const expandDrill = async (
    folderId: string,
    name: string,
    upToIndex?: number,
  ) => {
    if (!expand || !wsId) {
      return;
    }
    const path =
      upToIndex !== undefined
        ? expand.path.slice(0, upToIndex + 1)
        : [...expand.path, { id: folderId, name }];
    setExpand({ ...expand, path, listing: null });
    try {
      const l = await ryfeApi.listFolder(wsId, folderId);
      setExpand((e) => (e ? { ...e, path, listing: l } : e));
    } catch {
      setError("Ordner kann nicht geöffnet werden.");
    }
  };

  // AP-KI-Tags — set (or clear) a document's category tag from the list.
  const saveCategory = async (fileId: string, value: string) => {
    if (!wsId) {
      return;
    }
    const category = value === "" ? null : value;
    try {
      await ryfeApi.setFileMetadata(wsId, fileId, { category });
      setCategories((prev) => ({
        ...prev,
        [fileId]: { category, has_md: prev[fileId]?.has_md ?? false },
      }));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  useEffect(() => {
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  const atRoot = !folderId;
  const myRole = listing?.my_role;
  const canWriteHere = canWriteRole(myRole);

  // CC_05 Block 4 — client-side column sort. Folders and files sort
  // independently so folders stay grouped above files; columns a folder lacks
  // (size, dates) sink to a stable order within the folder group.
  const { sort, toggle, indicator } = useTableSort();
  const folderVal = (f: FolderChild, key: string): unknown => {
    switch (key) {
      case "name":
        return f.name;
      case "kind":
        return "Ordner";
      case "shared":
        return f.shared_count ?? 0;
      case "role":
        return f.my_role;
      default:
        return null; // size/dates — folders have none
    }
  };
  const fileVal = (f: FileMeta, key: string): unknown => {
    switch (key) {
      case "name":
        return f.name;
      case "kind":
        return typeLabel(f);
      case "size":
        return f.size_bytes ?? 0;
      case "updated":
        return f.updated_at;
      case "opened":
        return f.last_opened_at;
      case "role":
        return myRole;
      default:
        // AP-4 (P22) — custom columns are filterable too: an unknown key is a
        // custom column id → resolve its shared per-file value.
        return (fileVals[f.id] || {})[key] ?? null;
    }
  };
  // CC_05 Block 5 — client-side filter (persisted presets via FilterBar).
  const [filter, setFilter] = useState<FilterConfig>(EMPTY_FILTER);
  // R4 (Zwischenfeedback) — „Ordner ausblenden": flach über den ganzen Teilbaum
  // filtern. Der Server jagt jede Datei durch den Guard (read, fail-closed).
  const [flat, setFlat] = useState(false);
  const [flatFiles, setFlatFiles] = useState<FileMeta[] | null>(null);

  useEffect(() => {
    if (!flat || !wsId) {
      return;
    }
    let cancelled = false;
    setFlatFiles(null);
    ryfeApi
      .listFilesFlat(wsId, folderId)
      .then((r) => {
        if (!cancelled) {
          setFlatFiles(r.files);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFlatFiles([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [flat, wsId, folderId]);

  const sortedFolders =
    listing && !flat
      ? applyFilter(
          sortRows(listing.folders, sort, folderVal),
          filter,
          folderVal,
        )
      : [];
  // v1.6/D-KI — system files (e.g. the „Lenses" list) are already filtered out
  // server-side by the folder routes; the browser only ever sees normal files.
  // Im Flach-Modus kommen die Dateien aus dem guard-gefilterten Teilbaum-Endpoint.
  const sortedFiles = flat
    ? applyFilter(sortRows(flatFiles || [], sort, fileVal), filter, fileVal)
    : listing
    ? applyFilter(sortRows(listing.files, sort, fileVal), filter, fileVal)
    : [];

  const openFolder = (id: string) => navigate(`/files/${id}`);
  // CC_05 Block 1 (Audit #20, finalised): `kind` entscheidet, welcher Editor
  // öffnet — über dieselbe zentrale Weiche wie der Link-Pfad. Ein unbekannter
  // kind hat KEINEN Editor und landet bewusst NICHT im Board (sonst würde der
  // Board-Autosave fremden Inhalt überschreiben).
  const openBoard = (f: FileMeta) => {
    if (!wsId) {
      return;
    }
    // Welle B (Punkt 10) — Editor-Typen navigieren, Typen ohne Editor öffnen je
    // nach Art (URL extern, Bild im Tab, opaque als Download). Gemeinsame Logik.
    void openStoredFile(wsId, f).catch((err) =>
      setError(err instanceof ApiError ? err.message : "Öffnen fehlgeschlagen"),
    );
  };

  // CC_07 Block 4 (D-42) — one „Datei hinzufügen"-Pop-Up creates Board / Notiz /
  // Liste / Ordner. Replaces the four per-filetype buttons; the create goes into
  // the CURRENT folder (which may be a shared folder navigated into — Block 5).
  const [adding, setAdding] = useState(false);

  // CC_11 (D-62/D-63) — load the workspace's templates when the add dialog
  // opens, so „Aus Vorlage" always offers the current set.
  useEffect(() => {
    if (!adding || !wsId) {
      return;
    }
    let cancelled = false;
    ryfeApi
      .listTemplates(wsId)
      .then((r) => {
        if (!cancelled) {
          setTemplates(r.templates);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTemplates([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adding, wsId]);

  /** CC_11 (D-63) — „Neu aus Vorlage": fork into the CURRENT folder. */
  const createFromTemplate = async (templateId: string, name: string) => {
    if (!wsId || !listing) {
      return;
    }
    setBusy(true);
    try {
      const created = await ryfeApi.forkFile(wsId, templateId, {
        folderId: listing.id,
        name,
      });
      setAdding(false);
      const route = routeForKind(created.kind, created.id);
      if (route) {
        navigate(route);
      } else {
        await load();
      }
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Aus Vorlage fehlgeschlagen",
      );
    } finally {
      setBusy(false);
    }
  };

  /** CC_11 (D-62) — mark/unmark a file as template. */
  const toggleTemplate = async (t: Target) => {
    if (!wsId) {
      return;
    }
    try {
      await ryfeApi.setFileTemplateRole(
        wsId,
        t.id,
        t.templateRole === "template" ? null : "template",
      );
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Vorlage fehlgeschlagen",
      );
    }
  };

  /**
   * CC_11-Revision — generic snapshot producer for the files page (D-57): the
   * file's stored content becomes the uniform, client-encrypted view-only
   * snapshot — list = rows snapshot, md = content snapshot, board = the stored
   * scene as-is.
   */
  const makeFileSnapshot = (t: Target) => async (): Promise<string> => {
    if (!wsId) {
      throw new Error("no workspace");
    }
    if (t.fileKind === "list") {
      const snap = await ryfeApi.getList(wsId, t.id);
      return createEncryptedSnapshot({
        workspaceId: wsId,
        fileId: t.id,
        snapshot: {
          kind: "list",
          name: t.name,
          columns: snap.columns.map((c) => ({ id: c.id, name: c.name })),
          rows: snap.rows.map((r) => ({ id: r.id, seq: r.seq, data: r.data })),
        },
      });
    }
    const buf = await ryfeApi.getFileContent(wsId, t.id);
    const text = new TextDecoder().decode(new Uint8Array(buf));
    if (t.fileKind === "md") {
      return createEncryptedSnapshot({
        workspaceId: wsId,
        fileId: t.id,
        snapshot: { kind: "md", name: t.name, content: text },
      });
    }
    // Board: the stored blob IS the scene ({elements, appState, files}).
    return createEncryptedSnapshot({
      workspaceId: wsId,
      fileId: t.id,
      snapshot: JSON.parse(text) as Record<string, unknown>,
    });
  };
  const createEntry = async (kind: NewEntryKind, name: string) => {
    if (!wsId || !listing) {
      return;
    }
    setBusy(true);
    try {
      if (kind === "folder") {
        await ryfeApi.createFolder(wsId, listing.id, name);
        setAdding(false);
        await load();
      } else if (kind === "md") {
        const file = await ryfeApi.createFile(
          wsId,
          listing.id,
          name,
          "md",
          `# ${name}\n\n`,
          "text/markdown",
        );
        navigate(`/md/${file.id}`);
      } else if (kind === "list") {
        const file = await ryfeApi.createFile(
          wsId,
          listing.id,
          name,
          "list",
          "",
        );
        navigate(`/list/${file.id}`);
      } else {
        const empty = serializeScene([], {}, {});
        const file = await ryfeApi.createFile(
          wsId,
          listing.id,
          name,
          "board",
          empty,
        );
        navigate(`/board/${file.id}`);
      }
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Anlegen fehlgeschlagen",
      );
    } finally {
      setBusy(false);
    }
  };

  // Welle B (Punkt 10) — URL-Lesezeichen (kind='url'); Öffnen via openStoredFile.
  const createUrlFile = async (name: string, url: string) => {
    if (!wsId || !listing) {
      return;
    }
    setBusy(true);
    try {
      await createUrlBookmark(wsId, listing.id, name, url);
      setAdding(false);
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Anlegen fehlgeschlagen",
      );
    } finally {
      setBusy(false);
    }
  };

  // Welle B (Punkt 10) — Datei hochladen (Format→Typ via upload-map, Bytes 1:1).
  const uploadFile = async (file: File) => {
    if (!wsId || !listing) {
      return;
    }
    setBusy(true);
    try {
      const created = await uploadFileToFolder(wsId, listing.id, file);
      setAdding(false);
      // md/board/list bekommen einen Editor → direkt öffnen; opaque bleibt in
      // der Liste (Öffnen = Download).
      const route = routeForKind(created.kind, created.id);
      if (route) {
        navigate(route);
      } else {
        await load();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  // AP-7 (P17.2) — rekursiver Ordner-Upload: Struktur nachbauen + alle Dateien
  // hochladen (nicht nur ZIP). Nutzt die vorhandenen Endpoints.
  const uploadFolder = async (files: File[]) => {
    if (!wsId || !listing || files.length === 0) {
      return;
    }
    setBusy(true);
    try {
      await uploadFolderToFolder(wsId, listing.id, files);
      setAdding(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const rename = async (t: Target) => {
    if (!wsId) {
      return;
    }
    const name = window.prompt("Neuer Name", t.name);
    if (!name || name === t.name) {
      return;
    }
    try {
      if (t.kind === "file") {
        await ryfeApi.patchFile(wsId, t.id, { name });
      } else {
        await ryfeApi.patchFolder(wsId, t.id, { name });
      }
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Rename failed");
    }
  };

  /**
   * Datei duplizieren — frontend-only (kein Backend-Pfad nötig): der Inhalt wird
   * über die vorhandenen Endpunkte gelesen und als neue Datei im selben Ordner
   * angelegt. Blob-Typen (board/md/image/…) werden byte-genau kopiert; eine
   * Liste wird tief kopiert (Spalten + Zeilendaten). Relations-Verknüpfungen
   * einer Liste zeigen auf Zeilen-IDs und werden bewusst NICHT mitkopiert
   * (die neuen Zeilen haben neue IDs); alles andere bleibt erhalten.
   */
  const duplicate = async (t: Target) => {
    if (!wsId || !listing || t.kind !== "file") {
      return;
    }
    setBusy(true);
    try {
      const meta = await ryfeApi.getFile(wsId, t.id);
      const newName = `${meta.name} (Kopie)`;
      if (meta.kind === "list") {
        const snap = await ryfeApi.getList(wsId, t.id);
        const nf = await ryfeApi.createFile(
          wsId,
          listing.id,
          newName,
          "list",
          "",
        );
        const cols = [...snap.columns].sort((a, b) => a.position - b.position);
        for (const c of cols) {
          await ryfeApi.createListColumn(wsId, nf.id, {
            name: c.name,
            type: c.type,
            position: c.position,
            config: c.config,
          });
        }
        const rows = [...snap.rows].sort((a, b) => a.position - b.position);
        for (const r of rows) {
          await ryfeApi.createListRow(wsId, nf.id, {
            data: r.data,
            position: r.position,
          });
        }
      } else {
        const buf = await ryfeApi.getFileContent(wsId, t.id);
        await ryfeApi.createFile(
          wsId,
          listing.id,
          newName,
          meta.kind,
          buf,
          meta.content_type || "application/json",
        );
      }
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Duplizieren fehlgeschlagen",
      );
    } finally {
      setBusy(false);
    }
  };

  const countSubtree = async (id: string): Promise<number> => {
    if (!wsId) {
      return 0;
    }
    try {
      const l = await ryfeApi.listFolder(wsId, id);
      let n = l.files.length;
      for (const sub of l.folders) {
        n += 1 + (await countSubtree(sub.id));
      }
      return n;
    } catch {
      return 0;
    }
  };

  const remove = async (t: Target, permanent = false) => {
    if (!wsId) {
      return;
    }
    let msg: string;
    if (t.kind === "file") {
      // Block 9 — the reverse index names the parents a deletion would affect.
      let embedded: { id: string; name: string }[] = [];
      try {
        embedded = (await ryfeApi.getFile(wsId, t.id)).embedded_in || [];
      } catch {
        /* meta unavailable — plain confirmation */
      }
      // v1.3.0 Block 3: nur das ENDGÜLTIGE Löschen ist unwiderruflich —
      // der normale Weg schiebt in den Papierkorb und ist zurücknehmbar.
      const verb = permanent
        ? "endgültig löschen (nicht wiederherstellbar)"
        : "in den Papierkorb verschieben";
      msg =
        embedded.length > 0
          ? `Datei „${t.name}“ ${verb}? Diese Szene ist in ${
              embedded.length
            } Board${embedded.length > 1 ? "s" : ""} eingebettet: ${embedded
              .map((b) => b.name)
              .join(", ")}. Die Vorschauen dort zeigen dann „kein Zugriff“.`
          : `Datei „${t.name}“ ${verb}?`;
    } else {
      const n = await countSubtree(t.id);
      const verb = permanent
        ? "endgültig löschen (nicht wiederherstellbar)"
        : "in den Papierkorb verschieben";
      msg = `Ordner „${t.name}“ ${verb}? Betroffene Objekte: ${n}.`;
    }
    if (!window.confirm(msg)) {
      return;
    }
    try {
      if (t.kind === "file") {
        await ryfeApi.deleteFile(wsId, t.id, permanent);
      } else {
        await ryfeApi.deleteFolder(wsId, t.id, permanent);
      }
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Delete failed");
    }
  };

  /** v1.3.0 Block 3 — Archivieren = Umzug in den Archiv-Ordner. */
  const archive = async (t: Target) => {
    if (!wsId) {
      return;
    }
    try {
      if (t.kind === "file") {
        await ryfeApi.archiveFile(wsId, t.id);
      } else {
        await ryfeApi.archiveFolder(wsId, t.id);
      }
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Archivieren fehlgeschlagen",
      );
    }
  };

  /** Wiederherstellen holt zurück in den Workspace-Root. */
  const restore = async (t: Target) => {
    if (!wsId) {
      return;
    }
    try {
      if (t.kind === "file") {
        await ryfeApi.restoreFile(wsId, t.id);
      } else {
        await ryfeApi.restoreFolder(wsId, t.id);
      }
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Wiederherstellen fehlgeschlagen",
      );
    }
  };

  const onDrop = async (destFolderId: string, t: Target) => {
    setDragOver(null);
    if (!wsId || t.id === destFolderId) {
      return;
    }
    try {
      if (t.kind === "file") {
        await ryfeApi.patchFile(wsId, t.id, { folderId: destFolderId });
      } else {
        await ryfeApi.patchFolder(wsId, t.id, { parentId: destFolderId });
      }
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Move failed");
    }
  };

  // Clamp the menu against the viewport so it never opens off-screen — important
  // on the phone, where a right edge / bottom edge is easy to hit.
  const openMenuAt = (t: Target, x: number, y: number) => {
    const W = 180;
    const left = Math.max(8, Math.min(x, window.innerWidth - W - 8));
    setMenu({ x: left, y, t });
  };
  const openMenu = (e: React.MouseEvent, t: Target) => {
    e.preventDefault();
    e.stopPropagation();
    openMenuAt(t, e.clientX, e.clientY);
  };
  // Mobile entry point: the ⋮ button in the leading column opens the SAME menu
  // that a desktop right-click does (touch devices have no context menu).
  const openKebab = (e: React.MouseEvent, t: Target) => {
    e.preventDefault();
    e.stopPropagation();
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    openMenuAt(t, r.left, r.bottom + 2);
  };

  const dragProps = (t: Target) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      e.dataTransfer.setData("application/x-ryfe", JSON.stringify(t));
      e.dataTransfer.effectAllowed = "move";
    },
  });

  const dropProps = (folder: FolderChild) => ({
    onDragOver: (e: React.DragEvent) => {
      if (canWriteRole(folder.my_role)) {
        e.preventDefault();
        setDragOver(folder.id);
      }
    },
    onDragLeave: () => setDragOver((d) => (d === folder.id ? null : d)),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      const raw = e.dataTransfer.getData("application/x-ryfe");
      if (!raw) {
        return;
      }
      onDrop(folder.id, JSON.parse(raw) as Target);
    },
  });

  return (
    <div className="ryfe-page">
      <div className="ryfe-toolbar">
        <nav className="ryfe-breadcrumb" aria-label="Breadcrumb">
          <button
            className="ryfe-btn ryfe-btn--link"
            onClick={() => navigate("/files")}
          >
            Home
          </button>
          {/* CC_11-Revision — voller Pfad (root → … → aktueller Ordner), jede
              Zwischenstation klickbar; vorher stand hier nur der Zielordner. */}
          {!atRoot &&
            (listing?.path || [])
              .filter((p) => !p.is_root)
              .map((p, i, arr) => (
                <span key={p.id}>
                  {" / "}
                  {i === arr.length - 1 ? (
                    <span className="ryfe-breadcrumb__current">{p.name}</span>
                  ) : (
                    <button
                      className="ryfe-btn ryfe-btn--link"
                      onClick={() => openFolder(p.id)}
                    >
                      {p.name}
                    </button>
                  )}
                </span>
              ))}
        </nav>
        <FilterBar
          scope="files"
          fields={[
            { key: "name", label: "Name" },
            { key: "kind", label: "Typ" },
            { key: "size", label: "Größe (Bytes)" },
            { key: "updated", label: "Zuletzt geändert" },
            { key: "opened", label: "Zuletzt geöffnet" },
            { key: "role", label: "Rechte" },
            // AP-4 (P22) — filter by the workspace's custom columns too. A
            // select column offers its defined Auswahl-Optionen as choices.
            ...fileCols.map((c) => ({
              key: c.id,
              label: c.name,
              type: c.type,
              options:
                c.type === "select" &&
                Array.isArray((c.config as { options?: SelectOpt[] }).options)
                  ? (c.config as { options: SelectOpt[] }).options
                      .filter((o) => o.value.trim() !== "")
                      .map((o) => ({
                        value: o.value,
                        label: o.label || o.value,
                      }))
                  : undefined,
            })),
          ]}
          config={filter}
          onChange={setFilter}
        />
        {/* R4 — „Ordner ausblenden": die künstliche Hierarchie kurz auflösen,
            um über ALLE Dateien auf einer Ebene zu filtern (guard-gefiltert). */}
        <button
          className={
            flat ? "ryfe-btn ryfe-btn--primary" : "ryfe-btn ryfe-btn--ghost"
          }
          onClick={() => setFlat((v) => !v)}
          title="Ordner ausblenden und alle Dateien flach filtern"
          aria-pressed={flat}
        >
          {flat ? "🗂 Ordner zeigen" : "⤢ Ordner ausblenden"}
        </button>
        <span className="ryfe-topbar__spacer" />
        <button
          className="ryfe-btn ryfe-btn--primary"
          disabled={busy || !canWriteHere}
          onClick={() => setAdding(true)}
          title={canWriteHere ? "" : "Kein Schreibrecht hier"}
        >
          + Datei hinzufügen
        </button>
      </div>

      {error && (
        <div className="ryfe-auth__error" style={{ padding: "0.5rem 1rem" }}>
          {error}
        </div>
      )}

      {(flat ? flatFiles === null : !listing) ? (
        <div className="ryfe-muted">Loading…</div>
      ) : flat && sortedFiles.length === 0 ? (
        <div className="ryfe-muted">
          Keine Dateien
          {filter.conditions.length ? " (mit diesem Filter)" : ""}.
        </div>
      ) : !flat &&
        listing &&
        listing.folders.length === 0 &&
        listing.files.length === 0 ? (
        <div className="ryfe-muted">Dieser Ordner ist leer.</div>
      ) : null}
      {(flat
        ? sortedFiles.length > 0
        : listing &&
          (listing.folders.length > 0 || listing.files.length > 0)) && (
        <div className="ryfe-table-wrap">
          <table className="ryfe-table ryfe-table--files">
            <thead>
              <tr>
                <th className="ryfe-th--kebab" aria-label="Aktionen" />
                <SortableTh
                  sortKey="name"
                  label="Name"
                  toggle={toggle}
                  indicator={indicator}
                />
                <SortableTh
                  sortKey="kind"
                  label="Typ"
                  toggle={toggle}
                  indicator={indicator}
                />
                <th>MCP-Token</th>
                <SortableTh
                  sortKey="size"
                  label="Größe"
                  toggle={toggle}
                  indicator={indicator}
                />
                <SortableTh
                  sortKey="updated"
                  label="Zuletzt geändert"
                  toggle={toggle}
                  indicator={indicator}
                />
                <SortableTh
                  sortKey="opened"
                  label="Zuletzt geöffnet"
                  toggle={toggle}
                  indicator={indicator}
                />
                <th>Geteilt mit</th>
                <th>Freigabe</th>
                <SortableTh
                  sortKey="role"
                  label="Rechte"
                  toggle={toggle}
                  indicator={indicator}
                />
                {/* AP-4 (P11.4) — two auto system columns (derived, locked). */}
                <th>Erstellt von</th>
                <th>Zuletzt bearbeitet von</th>
                {/* AP-KI-Tags — category tag + metadata note per document. */}
                <th>Kategorie</th>
                {/* AP4 — workspace-shared user columns + "＋ Spalte". */}
                {fileCols.map((c) => (
                  <th key={c.id}>
                    <span className="ryfe-elmenu__wrap">
                      {c.private ? "🔒 " : ""}
                      {c.name}{" "}
                      {/* AP-4 (P11.1) — 3-Punkte-Menü je Custom Column. */}
                      <button
                        className="ryfe-chip"
                        aria-label={`Spalteneinstellungen ${c.name}`}
                        title="Spalteneinstellungen"
                        onClick={() =>
                          setColMenu((m) => (m === c.id ? null : c.id))
                        }
                      >
                        ⋮
                      </button>
                      {colMenu === c.id && (
                        <div className="ryfe-elmenu__sub" role="menu">
                          <button
                            className="ryfe-elmenu__item"
                            onClick={() => {
                              setEditingCol(c);
                              setColMenu(null);
                            }}
                          >
                            ⚙ Spalteneinstellungen
                          </button>
                          <button
                            className="ryfe-elmenu__item"
                            onClick={() => deleteFileColumn(c)}
                          >
                            🗑 Spalte löschen
                          </button>
                        </div>
                      )}
                    </span>
                  </th>
                ))}
                <th>
                  <button
                    className="ryfe-chip"
                    title="Eigene Spalte hinzufügen"
                    onClick={() => setAddingCol(true)}
                  >
                    ＋ Spalte
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedFolders.map((f) => {
                const t: Target = {
                  kind: "folder",
                  id: f.id,
                  name: f.name,
                  role: f.my_role,
                };
                const isOpen = expand?.id === f.id;
                return (
                  <Fragment key={f.id}>
                    <tr
                      className={`ryfe-row${
                        dragOver === f.id ? " ryfe-row--drop" : ""
                      }`}
                      onContextMenu={(e) => openMenu(e, t)}
                      {...dragProps(t)}
                      {...dropProps(f)}
                    >
                      <td className="ryfe-td--kebab">
                        <button
                          className="ryfe-kebab"
                          aria-label={`Aktionen für ${f.name}`}
                          title="Aktionen"
                          onClick={(e) => openKebab(e, t)}
                        >
                          ⋮
                        </button>
                      </td>
                      <td>
                        {/* Welle A — ▸ chevron expands the folder inline; the
                            name still navigates into the full view. */}
                        <button
                          className="ryfe-expand"
                          aria-label={`${
                            isOpen ? "Einklappen" : "Ausklappen"
                          }: ${f.name}`}
                          aria-expanded={isOpen}
                          onClick={() => void toggleExpand(f)}
                        >
                          {isOpen ? "▾" : "▸"}
                        </button>
                        {/* CC_11-Revision — Systemordner (Papierkorb/Archiv) sind
                            visuell abgegrenzt: eigenes Icon + System-Badge. */}
                        <button
                          className={`ryfe-linkish${
                            f.role ? " ryfe-linkish--system" : ""
                          }`}
                          onClick={() => openFolder(f.id)}
                        >
                          {f.role === "trash"
                            ? "🗑"
                            : f.role === "archive"
                            ? "🗄"
                            : "📁"}{" "}
                          {f.name}
                          {f.role && (
                            <span className="ryfe-sysbadge">System</span>
                          )}
                        </button>
                      </td>
                      <td>{f.role ? "Systemordner" : "Ordner"}</td>
                      <td>
                        <button
                          className="ryfe-chip"
                          title="MCP-Token für diesen Ordner erstellen"
                          onClick={() =>
                            setTokenFolder({ id: f.id, name: f.name })
                          }
                          disabled={!canManageRole(f.my_role)}
                        >
                          {f.mcp_token_count ?? 0} ＋
                        </button>
                      </td>
                      <td>—</td>
                      <td>—</td>
                      <td>—</td>
                      <td>{f.shared_count ?? 0}</td>
                      <td>
                        <button
                          className="ryfe-chip"
                          title="Freigabeeinstellung"
                          onClick={() =>
                            setShareTarget({
                              kind: "folder",
                              id: f.id,
                              name: f.name,
                            })
                          }
                          disabled={!canManageRole(f.my_role)}
                        >
                          Freigabe…
                        </button>
                      </td>
                      <td>{f.my_role || "—"}</td>
                      {/* AP-4 (P11.4) — author/editor apply to files; folders blank. */}
                      <td>—</td>
                      <td>—</td>
                      {/* AP-KI-Tags — categories apply to files; folders blank. */}
                      <td>—</td>
                      {/* AP4 — user columns apply to files; folders show blanks. */}
                      {fileCols.map((c) => (
                        <td key={c.id}>—</td>
                      ))}
                      <td />
                    </tr>
                    {isOpen && expand && (
                      <tr className="ryfe-row ryfe-row--expand">
                        <td colSpan={14 + fileCols.length}>
                          {!expand.listing ? (
                            <div className="ryfe-muted">Laden…</div>
                          ) : (
                            <div className="ryfe-sharedtree">
                              {expand.path.length > 1 && (
                                <nav
                                  className="ryfe-breadcrumbs"
                                  aria-label="Pfad"
                                >
                                  {expand.path.map((p, idx) =>
                                    idx < expand.path.length - 1 ? (
                                      <span key={p.id}>
                                        <button
                                          className="ryfe-linkish"
                                          onClick={() =>
                                            void expandDrill(p.id, p.name, idx)
                                          }
                                        >
                                          {p.name}
                                        </button>
                                        {" / "}
                                      </span>
                                    ) : (
                                      <span key={p.id}>{p.name}</span>
                                    ),
                                  )}
                                </nav>
                              )}
                              {expand.listing.folders.map((sf) => (
                                <button
                                  key={sf.id}
                                  className="ryfe-linkish"
                                  onClick={() =>
                                    void expandDrill(sf.id, sf.name)
                                  }
                                >
                                  📁 {sf.name}
                                </button>
                              ))}
                              {expand.listing.files
                                .filter((sfile) => !(sfile as FileMeta).system)
                                .map((sfile) => (
                                  <button
                                    key={sfile.id}
                                    className="ryfe-linkish"
                                    onClick={() => openBoard(sfile)}
                                  >
                                    {sfile.kind === "board" ? "🗒" : "📄"}{" "}
                                    {sfile.name}
                                  </button>
                                ))}
                              {expand.listing.folders.length === 0 &&
                                expand.listing.files.filter(
                                  (sfile) => !(sfile as FileMeta).system,
                                ).length === 0 && (
                                  <div className="ryfe-muted">leer</div>
                                )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {sortedFiles.map((f) => {
                const t: Target = {
                  kind: "file",
                  id: f.id,
                  name: f.name,
                  role: myRole,
                  fileKind: f.kind,
                  templateRole: (f as FileMeta).role ?? null,
                };
                return (
                  <tr
                    key={f.id}
                    className="ryfe-row"
                    onContextMenu={(e) => openMenu(e, t)}
                    {...dragProps(t)}
                  >
                    <td className="ryfe-td--kebab">
                      <button
                        className="ryfe-kebab"
                        aria-label={`Aktionen für ${f.name}`}
                        title="Aktionen"
                        onClick={(e) => openKebab(e, t)}
                      >
                        ⋮
                      </button>
                    </td>
                    <td>
                      <button
                        className="ryfe-linkish"
                        onClick={() => openBoard(f)}
                      >
                        {f.name}
                      </button>
                      {/* R4 — im Flach-Modus zeigt die Namenszelle den Ordner,
                          aus dem die Datei stammt (Herkunfts-Hinweis). */}
                      {flat && f.folder_name && (
                        <span className="ryfe-row__origin">
                          🗂 {f.folder_name}
                        </span>
                      )}
                    </td>
                    <td>{typeLabel(f)}</td>
                    <td>—</td>
                    <td>{fmtSize(f.size_bytes)}</td>
                    <td>{fmtDate(f.updated_at)}</td>
                    <td>{fmtDate(f.last_opened_at)}</td>
                    <td>—</td>
                    <td>
                      <button
                        className="ryfe-chip"
                        title="Freigabeeinstellung"
                        onClick={() =>
                          setShareTarget({
                            kind: "file",
                            id: f.id,
                            name: f.name,
                          })
                        }
                        disabled={!canManageRole(myRole)}
                      >
                        Freigabe…
                      </button>
                    </td>
                    <td>{myRole || "—"}</td>
                    {/* AP-4 (P11.4) — auto author + last editor (derived, locked). */}
                    <td className="ryfe-td--who">
                      {f.created_by_email || "—"}
                    </td>
                    <td className="ryfe-td--who">
                      {f.updated_by_email || "—"}
                    </td>
                    {/* AP-KI-Tags — the category tag (dropdown) + a note button
                        that opens the metadata editor. */}
                    <td>
                      <div className="ryfe-cat">
                        <select
                          className="ryfe-cat__select"
                          aria-label={`Kategorie-Tag für ${f.name}`}
                          value={categories[f.id]?.category ?? ""}
                          disabled={!canWriteRole(myRole)}
                          onChange={(e) => saveCategory(f.id, e.target.value)}
                        >
                          <option value="">— ohne —</option>
                          {KI_CATEGORIES.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        <button
                          className="ryfe-chip"
                          title="Metadaten bearbeiten"
                          aria-label={`Metadaten für ${f.name}`}
                          onClick={() =>
                            setMetaTarget({ id: f.id, name: f.name })
                          }
                        >
                          {categories[f.id]?.has_md ? "📝" : "＋"}
                        </button>
                      </div>
                    </td>
                    {/* AP4 — the user's shared columns, editable inline. A
                        select column renders its defined Auswahl-Optionen as a
                        dropdown; every other type stays a free text input. */}
                    {fileCols.map((c) => {
                      const raw = (fileVals[f.id] || {})[c.id];
                      const cur = String(raw ?? "");
                      const opts = Array.isArray(
                        (c.config as { options?: unknown }).options,
                      )
                        ? ((c.config as { options?: SelectOpt[] })
                            .options as SelectOpt[])
                        : [];
                      const label = `${c.name} für ${f.name}`;
                      return (
                        <td key={c.id}>
                          {c.type === "select" ? (
                            <select
                              className="ryfe-cell-input"
                              value={cur}
                              onChange={(e) =>
                                saveFileCell(f.id, c, e.target.value)
                              }
                              aria-label={label}
                            >
                              <option value="">—</option>
                              {opts
                                .filter((o) => o.value.trim() !== "")
                                .map((o) => (
                                  <option key={o.value} value={o.value}>
                                    {o.label || o.value}
                                  </option>
                                ))}
                            </select>
                          ) : c.type === "checkbox" ? (
                            // Boolean-Wert als echte Checkbox (vorher fälschlich
                            // ein Textfeld) — speichert true/false, nicht "true".
                            <input
                              type="checkbox"
                              className="ryfe-cell-check"
                              checked={raw === true || raw === "true"}
                              onChange={(e) =>
                                saveFileCell(f.id, c, e.target.checked)
                              }
                              aria-label={label}
                            />
                          ) : c.type === "number" ? (
                            <input
                              className="ryfe-cell-input"
                              type="number"
                              defaultValue={cur}
                              onBlur={(e) =>
                                saveFileCell(
                                  f.id,
                                  c,
                                  e.target.value === ""
                                    ? ""
                                    : Number(e.target.value),
                                )
                              }
                              aria-label={label}
                            />
                          ) : c.type === "date" ? (
                            <input
                              className="ryfe-cell-input"
                              type="date"
                              defaultValue={cur}
                              onBlur={(e) =>
                                saveFileCell(f.id, c, e.target.value)
                              }
                              aria-label={label}
                            />
                          ) : (
                            <input
                              className="ryfe-cell-input"
                              defaultValue={cur}
                              onBlur={(e) =>
                                saveFileCell(f.id, c, e.target.value)
                              }
                              aria-label={label}
                            />
                          )}
                        </td>
                      );
                    })}
                    <td />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Welle A (Datei-Master) — „Meine Dateien" is the master; the second
          table „Für mich freigegeben" lives on the same page (root only), both
          in the same style with inline-expandable folders. Im Flach-Modus
          ausgeblendet — dort filtert man gezielt den eigenen Baum. */}
      {atRoot && !flat && <RyfeSharedWithMe embedded />}

      {menu && (
        <ul
          className="ryfe-context"
          style={{ top: menu.y, left: menu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <li>
            <button
              disabled={!canWriteRole(menu.t.role)}
              onClick={() => {
                setMoving(menu.t);
                setMenu(null);
              }}
            >
              Verschieben
            </button>
          </li>
          <li>
            <button
              disabled={!canWriteRole(menu.t.role)}
              onClick={() => {
                rename(menu.t);
                setMenu(null);
              }}
            >
              Umbenennen
            </button>
          </li>
          {menu.t.kind === "folder" && listing?.in_role !== "trash" && (
            <li>
              <button
                disabled={!canWriteRole(menu.t.role)}
                onClick={() => {
                  const f =
                    listing?.folders.find((x) => x.id === menu.t.id) || null;
                  setKiFolder(f);
                  setMenu(null);
                }}
              >
                KI-Ordner…
              </button>
            </li>
          )}
          {menu.t.kind === "file" && listing?.in_role !== "trash" && (
            <li>
              <button
                disabled={busy || !canWriteHere}
                title={canWriteHere ? "" : "Kein Schreibrecht in diesem Ordner"}
                onClick={() => {
                  duplicate(menu.t);
                  setMenu(null);
                }}
              >
                Duplizieren
              </button>
            </li>
          )}
          {/* CC_11-Revision — externes Teilen (Mail + Snapshot) direkt aus
              „Meine Dateien"; die interne Rechteverwaltung bleibt „Freigabe…". */}
          {menu.t.kind === "file" && listing?.in_role !== "trash" && (
            <li>
              <button
                disabled={!canManageRole(menu.t.role)}
                title={
                  canManageRole(menu.t.role)
                    ? ""
                    : "Teilen erfordert Eigentümer-Rechte"
                }
                onClick={() => {
                  setShareMenu(menu.t);
                  setMenu(null);
                }}
              >
                Teilen…
              </button>
            </li>
          )}
          {/* CC_11 (D-62) — Vorlagen-Markierung (Datei ist normale files-Zeile). */}
          {menu.t.kind === "file" && listing?.in_role !== "trash" && (
            <li>
              <button
                disabled={!canWriteRole(menu.t.role)}
                onClick={() => {
                  void toggleTemplate(menu.t);
                  setMenu(null);
                }}
              >
                {menu.t.templateRole === "template"
                  ? "Vorlage entfernen"
                  : "Als Vorlage markieren"}
              </button>
            </li>
          )}
          {/* v1.8 AP-5 (P21) — Favoriten-Markierung (per User, read-level). */}
          {menu.t.kind === "file" && listing?.in_role !== "trash" && (
            <li>
              <button
                onClick={() => {
                  void toggleFavorite(menu.t);
                  setMenu(null);
                }}
              >
                {favIds.has(menu.t.id) ? "★ Aus Favoriten" : "☆ Zu Favoriten"}
              </button>
            </li>
          )}
          {/* v1.3.0 Block 3 (D-19): im Papierkorb gibt es Wiederherstellen und
              Endgültig löschen statt Archivieren/Löschen. */}
          {listing?.in_role !== "trash" && (
            <li>
              <button
                disabled={
                  menu.t.kind === "folder"
                    ? !canManageRole(menu.t.role)
                    : !canWriteRole(menu.t.role)
                }
                onClick={() => {
                  archive(menu.t);
                  setMenu(null);
                }}
              >
                Archivieren
              </button>
            </li>
          )}
          {(listing?.in_role === "trash" || listing?.in_role === "archive") && (
            <li>
              <button
                disabled={!canWriteRole(menu.t.role)}
                onClick={() => {
                  restore(menu.t);
                  setMenu(null);
                }}
              >
                Wiederherstellen
              </button>
            </li>
          )}
          <li>
            <button
              className="ryfe-context__danger"
              disabled={
                menu.t.kind === "folder"
                  ? !canManageRole(menu.t.role)
                  : !canWriteRole(menu.t.role)
              }
              onClick={() => {
                remove(menu.t, listing?.in_role === "trash");
                setMenu(null);
              }}
            >
              {listing?.in_role === "trash" ? "Endgültig löschen" : "Löschen"}
            </button>
          </li>
        </ul>
      )}

      {adding && (
        <AddFileDialog
          busy={busy}
          onClose={() => setAdding(false)}
          onCreate={createEntry}
          templates={templates}
          onCreateFromTemplate={createFromTemplate}
          onCreateUrl={createUrlFile}
          onUpload={uploadFile}
          onUploadFolder={uploadFolder}
        />
      )}

      {/* CC_11-Revision — externes Teilen (Mail + Snapshot) aus der Dateiliste. */}
      {shareMenu && wsId && (
        <ShareSubmenu
          target={{ kind: "file", id: shareMenu.id, name: shareMenu.name }}
          wsId={wsId}
          onClose={() => setShareMenu(null)}
          onInviteByEmail={async (email, role) => {
            await ryfeApi.inviteToFile(
              wsId,
              shareMenu.id,
              email,
              role === "editor" ? "editor" : "viewer",
            );
          }}
          onCreateSnapshot={makeFileSnapshot(shareMenu)}
        />
      )}

      {kiFolder && wsId && (
        <KiFolderDialog
          ws={wsId}
          folderId={kiFolder.id}
          folderName={kiFolder.name}
          initialType={kiFolder.ki_ordner_type || "free"}
          initialIsKiOrdner={!!kiFolder.is_ki_ordner}
          onClose={() => {
            setKiFolder(null);
            load();
          }}
        />
      )}
      {moving && wsId && (
        <MoveDialog
          wsId={wsId}
          target={moving}
          onClose={() => setMoving(null)}
          onMoved={() => {
            setMoving(null);
            load();
          }}
        />
      )}

      {shareTarget && wsId && (
        <ShareSettingsDialog
          wsId={wsId}
          target={shareTarget}
          onClose={() => {
            setShareTarget(null);
            load(); // refresh the "Geteilt mit" counters
          }}
        />
      )}

      {tokenFolder && wsId && (
        <CreateTokenDialog
          wsId={wsId}
          folderId={tokenFolder.id}
          folderName={tokenFolder.name}
          onClose={() => setTokenFolder(null)}
          onCreated={() => {
            setTokenFolder(null);
            load();
          }}
        />
      )}
      {/* AP4 — add (or edit) a workspace-shared/private column in „Meine
          Dateien". Bearbeiten pflegt u. a. die Auswahl-Optionen nach. */}
      {(addingCol || editingCol) && wsId && (
        <FileColumnDialog
          wsId={wsId}
          folderId={listing?.id}
          initial={editingCol}
          onClose={() => {
            setAddingCol(false);
            setEditingCol(null);
          }}
          onSaved={() => {
            setAddingCol(false);
            setEditingCol(null);
            void load();
          }}
        />
      )}
      {/* AP-KI-Tags — the per-document metadata editor (category + MD note). */}
      {metaTarget && wsId && (
        <MetadataDialog
          wsId={wsId}
          fileId={metaTarget.id}
          fileName={metaTarget.name}
          canWrite={canWriteRole(myRole)}
          onClose={() => setMetaTarget(null)}
          onSaved={() => {
            setMetaTarget(null);
            void load();
          }}
        />
      )}
    </div>
  );
};

const FileColumnDialog = ({
  wsId,
  folderId,
  initial,
  onClose,
  onSaved,
}: {
  wsId: string;
  // v1.7 — the folder the new column is scoped to (its subtree). Undefined = root.
  folderId?: string;
  initial?: FileColumn | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const cfg0 = (initial?.config ?? {}) as Record<string, unknown>;
  const [name, setName] = useState(initial?.name ?? "");
  const [type, setType] = useState(initial?.type ?? "text");
  const [priv, setPriv] = useState(Boolean(initial?.private));
  // Auswahl-Optionen — 1:1 aus dem Listen-Spaltendialog wiederverwendet.
  const [options, setOptions] = useState<SelectOpt[]>(
    Array.isArray(cfg0.options) ? (cfg0.options as SelectOpt[]) : [],
  );
  const [selectDefault, setSelectDefault] = useState(
    String(cfg0.default ?? ""),
  );
  const [multiple, setMultiple] = useState(Boolean(cfg0.multiple));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const editing = !!initial;

  const buildConfig = (): Record<string, unknown> =>
    type === "select"
      ? {
          options: options.filter((o) => o.value.trim() !== ""),
          default: selectDefault || undefined,
          multiple,
        }
      : {};

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (editing) {
        // Typ ist unveränderlich (er würde die gespeicherten Werte stranden);
        // Name + config (Optionen) + privat/nicht-privat lassen sich nachträglich
        // in den Spalteneinstellungen pflegen (AP-4 P11.1).
        await ryfeApi.updateFileColumn(wsId, initial!.id, {
          name,
          config: buildConfig(),
          private: priv,
        });
      } else {
        await ryfeApi.addFileColumn(wsId, {
          name,
          type,
          config: buildConfig(),
          private: priv,
          folderId,
        });
      }
      onSaved();
    } catch (e) {
      setErr((e as Error).message || "Speichern fehlgeschlagen");
      setBusy(false);
    }
  };

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        <h2 className="ryfe-panel__title">
          {editing
            ? `Spalteneinstellungen · „${initial?.name ?? ""}“`
            : "Spalte hinzufügen"}
        </h2>
        <label className="ryfe-field">
          <span>Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z. B. Kategorie"
          />
        </label>
        <label className="ryfe-field">
          <span>Typ</span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            disabled={editing}
            title={editing ? "Typ ist nach dem Anlegen fest" : ""}
          >
            <option value="text">Text</option>
            <option value="number">Zahl</option>
            <option value="checkbox">Checkbox</option>
            <option value="date">Datum</option>
            <option value="select">Auswahl</option>
          </select>
        </label>
        {type === "select" && (
          <SelectConfigEditor
            options={options}
            setOptions={setOptions}
            selectDefault={selectDefault}
            setSelectDefault={setSelectDefault}
            multiple={multiple}
            setMultiple={setMultiple}
          />
        )}
        {/* AP-4 (P11.1) — privat/nicht-privat, jetzt auch nachträglich in den
            Spalteneinstellungen umschaltbar. Mock-Label „Nicht privat". (Die
            Toggles „Tag-Vererbung vom Ordner" (D-136) und „Editoren dürfen Werte
            setzen" (P11.3/D-128) aus dem Mock folgen mit ihren Backends.) */}
        <label className="ryfe-check">
          <input
            type="checkbox"
            checked={!priv}
            onChange={(e) => setPriv(!e.target.checked)}
          />
          <span>Nicht privat (in „Für mich freigegeben“ sichtbar)</span>
        </label>
        {err && <div className="ryfe-auth__error">{err}</div>}
        <div className="ryfe-modal__actions">
          <span className="ryfe-topbar__spacer" />
          <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="ryfe-btn ryfe-btn--primary"
            onClick={save}
            disabled={busy || !name.trim()}
          >
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
};

// v1.6 / D-KI (AP-KI-Tags) — the per-document metadata editor. A slim editor for
// the category tag and the free-form markdown note (stored as an MD blob on S3).
// The same tag/note the AI reads and writes over MCP.
const MetadataDialog = ({
  wsId,
  fileId,
  fileName,
  canWrite,
  onClose,
  onSaved,
}: {
  wsId: string;
  fileId: string;
  fileName: string;
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const [category, setCategory] = useState<string>("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    ryfeApi
      .getFileMetadata(wsId, fileId)
      .then((m) => {
        if (!cancelled) {
          setCategory(m.category ?? "");
          setNote(m.metadata_md ?? "");
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setErr((e as Error).message || "Laden fehlgeschlagen");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [wsId, fileId]);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await ryfeApi.setFileMetadata(wsId, fileId, {
        category: category === "" ? null : category,
        metadata_md: note,
      });
      onSaved();
    } catch (e) {
      setErr((e as Error).message || "Speichern fehlgeschlagen");
      setBusy(false);
    }
  };

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        <h2 className="ryfe-panel__title">Metadaten — {fileName}</h2>
        {loading ? (
          <p className="ryfe-muted">Lädt…</p>
        ) : (
          <>
            <label className="ryfe-field">
              <span>Kategorie</span>
              <select
                value={category}
                disabled={!canWrite}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">— ohne —</option>
                {KI_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="ryfe-field">
              <span>Notiz (Markdown)</span>
              <textarea
                className="ryfe-meta__note"
                rows={10}
                value={note}
                disabled={!canWrite}
                onChange={(e) => setNote(e.target.value)}
                placeholder="# Kontext&#10;Freitext in Markdown …"
                aria-label="Metadaten-Notiz"
              />
            </label>
          </>
        )}
        {err && <div className="ryfe-auth__error">{err}</div>}
        <div className="ryfe-modal__actions">
          <span className="ryfe-topbar__spacer" />
          <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
            {canWrite ? "Abbrechen" : "Schließen"}
          </button>
          {canWrite && (
            <button
              className="ryfe-btn ryfe-btn--primary"
              onClick={save}
              disabled={busy || loading}
            >
              Speichern
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
